import type { TableSort, TaxEntryAward, TaxEntryLine, TaxEntryList } from '@myrewards/contracts';
import { netToTax, taxEntryFigures } from '@myrewards/domain';
import {
  formatDate,
  plural,
  sortRows,
  type ColumnSort,
  type ListFilter,
  type SortColumn,
} from '../../ui';

/**
 * One person's two boxes (APP-101 §2): the taxes from ADP Quick Calc and the net pay. Type either
 * and the other follows, so they always add up to the gross; a figure above the gross says why.
 */
export interface TaxDraft {
  readonly taxCents: number | null;
  readonly netPayCents: number | null;
  /** The box last typed in; null for the saved figure. */
  readonly edited: 'tax' | 'net' | null;
  readonly problem: string | null;
}

const BLANK: TaxDraft = { taxCents: null, netPayCents: null, edited: null, problem: null };

export function taxDraftFrom(cashCents: number, taxCents: number | null): TaxDraft {
  if (taxCents === null) return BLANK;
  return { taxCents, netPayCents: cashCents - taxCents, edited: null, problem: null };
}

export function typeTaxes(draft: TaxDraft, cashCents: number, cents: number | null): TaxDraft {
  if (cents === null) return { ...BLANK, edited: 'tax' };
  const figures = taxEntryFigures(cashCents, cents);
  return figures.ok
    ? { ...figures.value, edited: 'tax', problem: null }
    : {
        ...draft,
        taxCents: cents,
        netPayCents: null,
        edited: 'tax',
        problem: 'Taxes can’t be more than the gross.',
      };
}

export function typeNetPay(draft: TaxDraft, cashCents: number, cents: number | null): TaxDraft {
  if (cents === null) return { ...BLANK, edited: 'net' };
  const figures = netToTax(cashCents, cents);
  return figures.ok
    ? { ...figures.value, edited: 'net', problem: null }
    : {
        ...draft,
        taxCents: null,
        netPayCents: cents,
        edited: 'net',
        problem: 'Net pay can’t be more than the gross.',
      };
}

/** A figure that's whole, within the gross and different from the one saved. */
export function canSave(draft: TaxDraft, savedTaxCents: number | null): boolean {
  return draft.problem === null && draft.taxCents !== null && draft.taxCents !== savedTaxCents;
}

/** "Entered by Meredith Okonkwo · Oct 19, 2026": what the app recorded, never "From ADP". */
export function enteredWords(line: TaxEntryLine): string | null {
  return line.entered === null
    ? null
    : `Entered by ${line.entered.by} · ${formatDate(line.entered.at)}`;
}

const DAY = 86_400_000;

/**
 * The hero's line: people left, on how many PAAs, and the oldest approval still waiting. Before
 * any PAA has reached the team it says what will land here, not that nothing is left (APP-093).
 */
export function heroWords(list: TaxEntryList): string {
  const waiting = list.awards.filter((a) => a.toEnter > 0);
  if (!list.reachedTeam && list.awards.length === 0)
    return 'No PAA has reached the team yet. Each person’s taxes from ADP Quick Calc are entered here once a PAA with cash is approved.';
  if (list.toEnter === 0 || waiting.length === 0) return 'Nothing left to enter';
  const oldest = Math.min(...waiting.map((a) => Date.parse(a.approvedAt ?? list.asOf)));
  const days = Math.max(0, Math.floor((Date.parse(list.asOf) - oldest) / DAY));
  const wait =
    days === 0 ? 'oldest approved today' : `oldest waiting ${plural(days, 'day', 'days')}`;
  return `${plural(list.toEnter, 'person', 'people')} left on ${plural(waiting.length, 'PAA', 'PAAs')} · ${wait}`;
}

// --- Sorting and filters (APP-149 D3-D5) ------------------------------------------------------
//
// The list is one PAA per group, one person per row. A person's column sorts the people within
// each PAA, and the PAAs by the first person each then has, so the person the sort puts first
// leads the list. The list opens as it does today: the oldest approval first, people in entry
// order.

type PersonColumn = 'person' | 'gross' | 'taxes' | 'net' | 'status';

const STATUS_WORDS = ['to enter first', 'entered first'] as const;

const PERSON_SORTS: Record<PersonColumn, ColumnSort<TaxEntryLine>> = {
  person: { value: (line) => line.name },
  gross: { value: (line) => line.cashCents },
  taxes: { value: (line) => line.taxCents },
  // Nothing entered yet: no net pay, so it goes last either way.
  net: { value: (line) => (line.taxCents === null ? null : line.cashCents - line.taxCents) },
  status: { value: (line) => (line.taxCents === null ? 0 : 1), words: STATUS_WORDS },
};

const PERSON_COLUMNS = Object.entries(PERSON_SORTS).map(([id, sort]) => ({ id, sort }));

export const TAX_DEFAULT_SORT: TableSort = { column: 'approved', dir: 'asc' };
export const TAX_DEFAULT_WORDS = 'oldest approval first';
const byAward = (award: TaxEntryAward): string => award.award.displayId;

const firstPerson = (column: PersonColumn) => (award: TaxEntryAward) => {
  const [first] = award.people;
  return first === undefined ? null : PERSON_SORTS[column].value(first);
};

/**
 * What the list sorts by, as the status line and the Sort by menu name it. Approval isn't a
 * heading: it's the order the list opens in.
 */
export const TAX_SORT_COLUMNS: readonly SortColumn<TaxEntryAward>[] = [
  {
    id: 'approved',
    header: 'Approval',
    sort: {
      value: (award) => (award.approvedAt === null ? null : Date.parse(award.approvedAt)),
      words: ['oldest first', 'newest first'],
    },
  },
  { id: 'person', header: 'Person', sort: { value: firstPerson('person') } },
  { id: 'gross', header: 'Gross', numeric: true, sort: { value: firstPerson('gross') } },
  { id: 'taxes', header: 'Taxes', numeric: true, sort: { value: firstPerson('taxes') } },
  { id: 'net', header: 'Net pay', numeric: true, sort: { value: firstPerson('net') } },
  {
    id: 'status',
    header: 'Status',
    sort: { value: firstPerson('status'), words: STATUS_WORDS },
  },
];

const isPersonColumn = (column: string): column is PersonColumn => column in PERSON_SORTS;

/** The PAAs in the sort's order, each with its people in it too. */
export function sortTaxAwards(awards: readonly TaxEntryAward[], sort: TableSort): TaxEntryAward[] {
  const column = sort.column;
  if (!isPersonColumn(column)) return sortRows(awards, TAX_SORT_COLUMNS, sort, byAward);
  const within = awards.map((award) => ({
    ...award,
    people: sortRows(award.people, PERSON_COLUMNS, sort),
  }));
  return sortRows(within, TAX_SORT_COLUMNS, sort, byAward);
}

/** The filters that mean something on Tax to enter (D5): every row is a PAA, so only the sector. */
export function taxFilters(
  awards: readonly TaxEntryAward[],
): Record<'sector', ListFilter<TaxEntryAward>> {
  const sectors = [
    ...new Set(awards.flatMap((award) => (award.sector === null ? [] : [award.sector]))),
  ].sort((a, b) => a.localeCompare(b));
  return {
    sector: {
      name: 'Sector',
      options: [
        { value: 'all', label: 'All sectors' },
        ...sectors.map((name) => ({ value: name, label: name })),
      ],
      passes: (award, value) => award.sector === value,
    },
  };
}

/** The params each Tax to enter table keeps in the address: the second's have its prefix. */
export const TAX_PARAMS: readonly string[] = [
  'sort',
  'dir',
  'sector',
  'enteredSort',
  'enteredDir',
  'enteredSector',
];
